import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import app, { db } from "../firebase";

const functions = getFunctions(app);

const ROLES_CREABLES = [
  { value: "admin", label: "Administrador" },
  { value: "Inspector Material Mayor", label: "Inspector Material Mayor" },
];

function rolLabel(rol) {
  return ROLES_CREABLES.find((r) => r.value === rol)?.label || rol || "Administrador";
}

export default function GestionAdministradores({ cuerpoActualId }) {
  const [admins, setAdmins] = useState([]);
  const [cuerpos, setCuerpos] = useState([]);
  const [companias, setCompanias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cuerpoId, setCuerpoId] = useState(cuerpoActualId || "");
  const [rol, setRol] = useState("admin");
  const [compania, setCompania] = useState("");

  const [editando, setEditando] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editCuerpoId, setEditCuerpoId] = useState("");
  const [editRol, setEditRol] = useState("admin");
  const [editCompania, setEditCompania] = useState("");
  const [editActivo, setEditActivo] = useState(true);

  useEffect(() => {
    if (cuerpoActualId) setCuerpoId(cuerpoActualId);
  }, [cuerpoActualId]);

  useEffect(() => {
    const unsubA = onSnapshot(collection(db, "administradores"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
      setAdmins(arr);
      setCargando(false);
    }, (e) => {
      console.error(e);
      setCargando(false);
      alert("No se pudo cargar el listado de administradores: " + (e?.message || e));
    });

    const unsubC = onSnapshot(collection(db, "cuerpos_bomberos"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setCuerpos(arr);
    });

    const unsubComp = onSnapshot(collection(db, "companias"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a,b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setCompanias(arr);
    });

    return () => { unsubA(); unsubC(); unsubComp(); };
  }, []);

  const cuerpoNombre = useMemo(() => {
    const m = new Map(cuerpos.map((c) => [c.id, c.nombre || c.id]));
    return (id) => id ? (m.get(id) || id) : "Sin Cuerpo";
  }, [cuerpos]);

  const companiasCrear = useMemo(() => companias.filter((c) => c.cuerpo_id === cuerpoId), [companias, cuerpoId]);

  async function crear() {
    if (!email.trim() || !password || !cuerpoId || !rol) {
      alert("Completa correo, contraseña, rol y Cuerpo de Bomberos.");
      return;
    }
    if (rol === "Inspector Material Mayor" && !compania) {
      alert("Para Inspector Material Mayor debes seleccionar una compañía.");
      return;
    }
    setProcesando("crear");
    try {
      const fn = httpsCallable(functions, "crearAdministradorSeguro");
      await fn({ email: email.trim(), password, cuerpoId, rol, compania: rol === "Inspector Material Mayor" ? compania : null });
      setEmail(""); setPassword(""); setRol("admin"); setCompania("");
      alert("Usuario administrativo creado correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo crear: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  function abrirEditar(a) {
    if (a.rol === "superadmin") {
      alert("El superadmin principal no se edita desde esta pantalla.");
      return;
    }
    setEditando(a);
    setEditEmail(a.email || "");
    setEditCuerpoId(a.cuerpo_id || "Huasco");
    setEditRol(a.rol === "Inspector Material Mayor" || a.rol === "inspector_material_mayor" ? "Inspector Material Mayor" : "admin");
    setEditCompania(a.compania || a.companiaNombre || "");
    setEditActivo(a.activo !== false);
  }

  const companiasEdit = useMemo(() => companias.filter((c) => c.cuerpo_id === editCuerpoId), [companias, editCuerpoId]);

  async function guardarEdicion() {
    if (!editando) return;
    if (!editEmail.trim() || !editCuerpoId || !editRol) {
      alert("Correo, rol y Cuerpo son obligatorios.");
      return;
    }
    if (editRol === "Inspector Material Mayor" && !editCompania) {
      alert("Para Inspector Material Mayor debes seleccionar una compañía.");
      return;
    }
    setProcesando(editando.id);
    try {
      const fn = httpsCallable(functions, "actualizarAdministradorSeguro");
      await fn({
        uid: editando.id,
        email: editEmail.trim(),
        cuerpoId: editCuerpoId,
        activo: editActivo,
        rol: editRol,
        compania: editRol === "Inspector Material Mayor" ? editCompania : null,
      });
      setEditando(null);
      alert("Administrador actualizado.");
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  async function cambiarClave(a) {
    if (a.rol === "superadmin") {
      alert("Usa el módulo Cambiar Clave para la cuenta superadmin.");
      return;
    }
    const p1 = window.prompt(`Nueva contraseña para ${a.email || a.id} (mínimo 6 caracteres):`);
    if (p1 == null) return;
    if (p1.length < 6) { alert("La contraseña debe tener al menos 6 caracteres."); return; }
    const p2 = window.prompt("Repite la nueva contraseña:");
    if (p2 !== p1) { alert("Las contraseñas no coinciden."); return; }

    setProcesando(a.id);
    try {
      const fn = httpsCallable(functions, "cambiarPasswordAdministradorSeguro");
      await fn({ uid: a.id, password: p1 });
      alert("Contraseña actualizada correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo cambiar la contraseña: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  async function eliminar(a) {
    if (a.rol === "superadmin") {
      alert("No se puede eliminar el superadmin desde esta pantalla.");
      return;
    }
    if (!window.confirm(`¿Eliminar definitivamente a ${a.email || a.id}?\n\nSe eliminará su cuenta de acceso y su documento administrativo.`)) return;
    setProcesando(a.id);
    try {
      const fn = httpsCallable(functions, "eliminarAdministradorSeguro");
      await fn({ uid: a.id });
      alert("Usuario eliminado correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  return (
    <div style={{ maxWidth: 1250 }}>
      <h2>Administradores</h2>
      <p>Crea y administra las cuentas de acceso de cada Cuerpo de Bomberos.</p>

      <div style={{ border: "1px solid #ddd", borderRadius: 10, padding: 16, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Crear nuevo usuario administrativo</h3>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,1fr) minmax(170px,1fr) minmax(190px,.8fr) minmax(220px,1fr) auto", gap: 8 }}>
          <input type="email" placeholder="Correo" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} />
          <select value={rol} onChange={(e) => { setRol(e.target.value); if (e.target.value !== "Inspector Material Mayor") setCompania(""); }}>
            {ROLES_CREABLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={cuerpoId} onChange={(e) => { setCuerpoId(e.target.value); setCompania(""); }}>
            <option value="">-- Cuerpo de Bomberos --</option>
            {cuerpos.map((c) => <option key={c.id} value={c.id}>{c.nombre || c.id}</option>)}
          </select>
          <button onClick={crear} disabled={procesando === "crear"}>{procesando === "crear" ? "Creando..." : "Crear"}</button>
        </div>
        {rol === "Inspector Material Mayor" && (
          <div style={{ marginTop: 10, maxWidth: 420 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Compañía del Inspector Material Mayor</label>
            <select value={compania} onChange={(e) => setCompania(e.target.value)} style={{ width: "100%" }}>
              <option value="">-- Seleccionar compañía --</option>
              {companiasCrear.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
            </select>
          </div>
        )}
      </div>

      {editando && (
        <div style={{ border: "2px solid #8aa8c2", borderRadius: 10, padding: 16, marginBottom: 24, background: "#f7fbff" }}>
          <h3 style={{ marginTop: 0 }}>Editar usuario</h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px,1fr) minmax(190px,.8fr) minmax(220px,1fr)", gap: 8 }}>
            <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            <select value={editRol} onChange={(e) => { setEditRol(e.target.value); if (e.target.value !== "Inspector Material Mayor") setEditCompania(""); }}>
              {ROLES_CREABLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select value={editCuerpoId} onChange={(e) => { setEditCuerpoId(e.target.value); setEditCompania(""); }}>
              {cuerpos.map((c) => <option key={c.id} value={c.id}>{c.nombre || c.id}</option>)}
            </select>
          </div>
          {editRol === "Inspector Material Mayor" && (
            <div style={{ marginTop: 10, maxWidth: 420 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Compañía</label>
              <select value={editCompania} onChange={(e) => setEditCompania(e.target.value)} style={{ width: "100%" }}>
                <option value="">-- Seleccionar compañía --</option>
                {companiasEdit.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <label style={{ display: "block", marginTop: 12 }}>
            <input type="checkbox" checked={editActivo} onChange={(e) => setEditActivo(e.target.checked)} /> Activo
          </label>
          <div style={{ marginTop: 12 }}>
            <button onClick={guardarEdicion} disabled={procesando === editando.id}>Guardar cambios</button>{" "}
            <button onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}

      <h3>Administradores registrados</h3>
      {cargando ? <p>Cargando administradores...</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>
              <th style={th}>Correo</th><th style={th}>Rol</th><th style={th}>Cuerpo</th><th style={th}>Compañía</th><th style={th}>Estado</th><th style={th}>Acciones</th>
            </tr></thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td style={td}>{a.email || a.id}</td>
                  <td style={td}>{a.rol === "superadmin" ? "Superadmin" : rolLabel(a.rol)}</td>
                  <td style={td}>{a.rol === "superadmin" ? "Todos los Cuerpos" : cuerpoNombre(a.cuerpo_id || "Huasco")}</td>
                  <td style={td}>{a.rol === "Inspector Material Mayor" || a.rol === "inspector_material_mayor" ? (a.compania || a.companiaNombre || "Sin asignar") : "—"}</td>
                  <td style={td}>{a.activo === false ? "Inactivo" : "Activo"}</td>
                  <td style={td}>
                    {a.rol === "superadmin" ? <span style={{ opacity: .65 }}>Cuenta principal</span> : <>
                      <button onClick={() => abrirEditar(a)} disabled={procesando === a.id}>Editar</button>{" "}
                      <button onClick={() => cambiarClave(a)} disabled={procesando === a.id}>Cambiar contraseña</button>{" "}
                      <button onClick={() => eliminar(a)} disabled={procesando === a.id} style={{ color: "#b00020" }}>Eliminar</button>
                    </>}
                  </td>
                </tr>
              ))}
              {!admins.length && <tr><td style={td} colSpan={6}>No hay administradores registrados.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { textAlign: "left", borderBottom: "2px solid #ccc", padding: 10 };
const td = { borderBottom: "1px solid #ddd", padding: 10, verticalAlign: "top" };
